import {BaseCommand} from './base.command';
import {ElementSnapshot, GraphLinkSnapshot} from '../interfaces/command.interface';
import {CytoscapeService} from '../../cy-services/cytoscape.service';
import {NodeService} from '../../../api/services/node.service';
import {firstValueFrom} from 'rxjs';

/**
 * CreateLinkCommand handles link creation.
 * The command is created AFTER the link is created, capturing its data.
 * Undo deletes the created link, redo restores it.
 */
export class CreateLinkCommand extends BaseCommand<ElementSnapshot> {
  /** Id of the link this command created — used to fold immediate follow-up edits. */
  public readonly createdEntityId: string;

  constructor(
    private cyService: CytoscapeService,
    private nodeService: NodeService,
    createdLink: GraphLinkSnapshot
  ) {
    super(
      'create-link',
      'Link Creation', // fallback description
      {
        before: {nodes: [], links: []},
        after: {nodes: [], links: [createdLink]}
      },
      {object: 'Link', verb: 'Creation'}
    );
    this.createdEntityId = createdLink.id;
  }

  async execute(): Promise<void> {
    // Called on redo - restore the link
    const cy = this.cyService.cy;
    if (!cy) return;

    const link = this.snapshot.after.links[0];
    if (!link) return;

    // Restore via backend
    await firstValueFrom(this.nodeService.restoreWithChildren([link.id]));

    // Add to Cytoscape
    if (cy.$(`#${link.id}`).length === 0) {
      cy.add({
        group: 'edges',
        data: {
          ...link.data,
          source: link.sourceId,
          target: link.targetId
        }
      });
    }

    cy.style().update();
  }

  async undo(): Promise<void> {
    const cy = this.cyService.cy;
    if (!cy) return;

    const link = this.snapshot.after.links[0];
    if (!link) return;

    // Remove from Cytoscape
    cy.$(`#${link.id}`).remove();

    // Soft-delete on backend
    await firstValueFrom(this.nodeService.deleteLink(link.id));
  }
}
